import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Video, Calendar, Copy, ExternalLink, Clock, Users } from "lucide-react";
import { useState } from "react";
import { NavLink } from "react-router-dom";

const MEETINGS = [
  {
    id: "1",
    titleEn: "Deal Review — Bâtir Pro",
    titleFr: "Révision deal — Bâtir Pro",
    date: "2026-06-13",
    time: "10:00",
    duration: 45,
    participants: ["AcquizGroup Coordinator", "Seller Broker"],
    link: "https://meet.google.com/xxx-yyyy-zzz",
    type: "deal_review",
  },
  {
    id: "2",
    titleEn: "Offer Discussion — Tech Acquisition",
    titleFr: "Discussion offre — Tech Acquisition",
    date: "2026-06-16",
    time: "14:30",
    duration: 30,
    participants: ["Deal Coordinator"],
    link: "https://meet.google.com/aaa-bbbb-ccc",
    type: "offer",
  },
];

export default function VideoConference() {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [copied, setCopied] = useState<string | null>(null);

  function copyLink(id: string, link: string) {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  const upcoming = MEETINGS.filter(m => new Date(`${m.date}T${m.time}`) >= new Date());
  const past     = MEETINGS.filter(m => new Date(`${m.date}T${m.time}`) < new Date());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium" style={{ color: "#1D314D" }}>
            {isFr ? "Visioconférence" : "Video Conference"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isFr ? "Réunions planifiées avec votre équipe AcquizGroup" : "Scheduled meetings with your AcquizGroup team"}
          </p>
        </div>
        <NavLink to="/dashboard/lender/calendar">
          <Button variant="outline" size="sm" className="gap-2">
            <Calendar className="h-4 w-4" />
            {isFr ? "Calendrier" : "Calendar"}
          </Button>
        </NavLink>
      </div>

      {/* Quick join panel */}
      <Card style={{ border: "0.5px solid rgba(29,49,77,0.15)", background: "rgba(29,49,77,0.03)" }}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center"
                 style={{ background: "rgba(206,171,101,0.12)", border: "1px solid rgba(206,171,101,0.25)" }}>
              <Video className="h-4 w-4" style={{ color: "#CEAB65" }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: "#1D314D" }}>
                {isFr ? "Rejoindre une réunion" : "Join a Meeting"}
              </p>
              <p className="text-xs text-muted-foreground">
                {isFr ? "Entrez un lien de réunion" : "Enter a meeting link"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={isFr ? "https://meet.google.com/..." : "https://meet.google.com/..."}
              className="flex-1 text-sm border rounded-md px-3 py-2"
              style={{ borderColor: "var(--color-border-tertiary)", background: "var(--color-background-primary)" }}
            />
            <Button size="sm" style={{ background: "#1D314D", color: "white" }} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              {isFr ? "Rejoindre" : "Join"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming meetings */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          {isFr ? "À venir" : "Upcoming"}
        </h2>
        {upcoming.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Video className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">
                {isFr ? "Aucune réunion planifiée" : "No upcoming meetings"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcoming.map((m) => (
              <Card key={m.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: "#1D314D" }}>
                        {isFr ? m.titleFr : m.titleEn}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(m.date).toLocaleDateString(isFr ? "fr-CA" : "en-US", { weekday: "short", month: "short", day: "numeric" })}
                          {" · "}{m.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {m.duration} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {m.participants.join(", ")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => copyLink(m.id, m.link)}
                        className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded border transition-all"
                        style={{ borderColor: "var(--color-border-tertiary)", color: "var(--color-text-secondary)" }}
                      >
                        <Copy className="h-3 w-3" />
                        {copied === m.id ? (isFr ? "Copié!" : "Copied!") : (isFr ? "Copier" : "Copy")}
                      </button>
                      <a href={m.link} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" style={{ background: "#1D314D", color: "white" }} className="gap-1.5 h-8 text-xs">
                          <Video className="h-3 w-3" />
                          {isFr ? "Rejoindre" : "Join"}
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Past meetings */}
      {past.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
            {isFr ? "Passées" : "Past"}
          </h2>
          <div className="space-y-2">
            {past.map((m) => (
              <div key={m.id}
                   className="flex items-center justify-between px-4 py-3 rounded-lg"
                   style={{ background: "var(--color-background-secondary)", opacity: 0.7 }}>
                <div>
                  <p className="text-sm">{isFr ? m.titleFr : m.titleEn}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(m.date).toLocaleDateString(isFr ? "fr-CA" : "en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {" · "}{m.duration} min
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}